/**
 * German places for the search autocomplete (client-side, zero backend calls per keystroke).
 *
 * Data source: GeoNames `cities1000` (populated places > 1000 inhabitants), licensed
 * CC-BY 4.0 — https://www.geonames.org/ . Attribution required (see Impressum).
 *
 * The JSON is a compact array-of-arrays `[name, state, lat, lng]`, pre-sorted by
 * population descending so a prefix match yields the most relevant place first.
 * It is served as a static asset from `public/data/` and fetched on first search
 * focus, so the ~100 KB gzipped payload never touches the initial page load (and is
 * kept out of the JS bundle / TypeScript type inference).
 */
export interface Place {
  name: string;
  state: string;
  lat: number;
  lng: number;
}

type RawPlace = [string, string, number, number];

let cache: Place[] | null = null;
let loading: Promise<Place[]> | null = null;

/** Lazy-load (and memoize) the German places dataset from the static asset. */
export function loadPlaces(): Promise<Place[]> {
  if (cache) return Promise.resolve(cache);
  if (!loading) {
    loading = fetch(`${process.env.PUBLIC_URL || ''}/data/de-places.json`)
      .then(r => r.json() as Promise<RawPlace[]>)
      .then(raw => {
        cache = raw.map(([name, state, lat, lng]) => ({ name, state, lat, lng }));
        return cache;
      })
      .catch(err => { loading = null; throw err; });
  }
  return loading;
}

/** Prefix search over the pre-sorted places list (popularity order preserved). */
export function searchPlaces(list: Place[], query: string, limit = 5): Place[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const out: Place[] = [];
  for (const p of list) {
    if (p.name.toLowerCase().startsWith(q)) {
      out.push(p);
      if (out.length >= limit) break;
    }
  }
  return out;
}
